import type { DifficultyKey } from './game-controller';

const STORAGE_KEY = 'letter-rain.settings';
const STORAGE_VERSION = 3;

interface LegacyStoredSettingsV1 {
  version: 1;
  difficulty?: DifficultyKey;
  fontScale?: string;
  colorMode?: string;
  lastScore?: number;
  lastStreak?: number;
  lastLives?: number;
  achievementsUnlocked?: string[];
  lastAchievementAt?: number;
  updatedAt?: number;
}

export interface StoredSettings {
  version: typeof STORAGE_VERSION;
  difficulty?: DifficultyKey;
  fontScale?: string;
  theme?: string;
  bestStreak?: number;
  achievementsUnlocked?: string[];
  lastAchievementAt?: number;
  tapMode?: boolean;
  updatedAt?: number;
}

export function loadSettings(): StoredSettings | null {
  if (!isStorageAvailable()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (isObjectWithVersion(parsed)) {
      const version = parsed.version;
      if (version === STORAGE_VERSION) {
        return normalizeCurrentSettings(parsed);
      }
      if (version === 2) {
        return migrateV2Settings(parsed);
      }
      if (version === 1) {
        return migrateLegacySettings(parsed as LegacyStoredSettingsV1);
      }
    }
  } catch (error) {
    console.warn('Letter Rain: failed to read settings', error);
  }
  return null;
}

export function saveSettings(partial: Partial<StoredSettings>): StoredSettings | null {
  if (!isStorageAvailable()) {
    return null;
  }
  const cleaned: Partial<StoredSettings> = {};
  for (const [key, value] of Object.entries(partial)) {
    if (value !== undefined) {
      (cleaned as Record<string, unknown>)[key] = value;
    }
  }

  const existing = loadSettings() ?? undefined;
  const next: StoredSettings = {
    ...(existing ?? {}),
    ...cleaned,
    version: STORAGE_VERSION,
    updatedAt: Date.now(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn('Letter Rain: failed to persist settings', error);
  }
  return next;
}

export function clearSettings(): void {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Letter Rain: failed to clear settings', error);
  }
}

function isStorageAvailable(): boolean {
  try {
    const key = '__letter-rain-test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function migrateLegacySettings(legacy: LegacyStoredSettingsV1): StoredSettings {
  return normalizeCurrentSettings({
    version: STORAGE_VERSION,
    difficulty: legacy.difficulty,
    theme: legacy.colorMode,
    bestStreak: legacy.lastStreak,
    achievementsUnlocked: legacy.achievementsUnlocked,
    lastAchievementAt: legacy.lastAchievementAt,
    updatedAt: legacy.updatedAt,
  });
}

function isObjectWithVersion(value: unknown): value is Record<string, unknown> & { version: number } {
  return typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>).version === 'number';
}

const DIFFICULTY_ALIASES: Record<string, DifficultyKey> = {
  zen: 'level1',
  gentle: 'level2',
  easy: 'level2',
  normal: 'level3',
  standard: 'level3',
  pulse: 'level4',
  brisk: 'level4',
  surge: 'level6',
  storm: 'level6',
  'level1': 'level1',
  'level2': 'level2',
  'level3': 'level3',
  'level4': 'level4',
  'level5': 'level5',
  'level6': 'level6',
};

function normalizeCurrentSettings(raw: Record<string, unknown>): StoredSettings {
  const next: StoredSettings = {
    version: STORAGE_VERSION,
  };
  const difficulty = normalizeDifficulty(raw.difficulty);
  if (difficulty) {
    next.difficulty = difficulty;
  }
  if (typeof raw.theme === 'string') {
    next.theme = normalizeTheme(raw.theme);
  }
  if (typeof raw.bestStreak === 'number') {
    next.bestStreak = raw.bestStreak;
  }
  if (Array.isArray(raw.achievementsUnlocked)) {
    next.achievementsUnlocked = raw.achievementsUnlocked.filter((id): id is string => typeof id === 'string');
  }
  if (typeof raw.lastAchievementAt === 'number') {
    next.lastAchievementAt = raw.lastAchievementAt;
  }
  if (typeof raw.tapMode === 'boolean') {
    next.tapMode = raw.tapMode;
  }
  if (typeof raw.updatedAt === 'number') {
    next.updatedAt = raw.updatedAt;
  }
  return next;
}

function migrateV2Settings(raw: Record<string, unknown>): StoredSettings {
  const upgraded = normalizeCurrentSettings({
    version: STORAGE_VERSION,
    difficulty: raw.difficulty,
    theme: raw.theme,
    bestStreak: raw.bestStreak,
    achievementsUnlocked: raw.achievementsUnlocked,
    lastAchievementAt: raw.lastAchievementAt,
    updatedAt: raw.updatedAt,
    tapMode: typeof raw.tapMode === 'boolean' ? raw.tapMode : true,
  });
  if (upgraded.tapMode === undefined) {
    upgraded.tapMode = true;
  }
  return upgraded;
}

function normalizeDifficulty(value: unknown): DifficultyKey | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  return DIFFICULTY_ALIASES[value] ?? undefined;
}

function normalizeTheme(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  switch (value) {
    case 'contrast':
      return 'aurora';
    case 'monochrome':
      return 'ember';
    case 'default':
      return 'midnight';
    default:
      return value;
  }
}
